import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { agenciesControllerGetMyAgencyV1 } from '@/api/generated/agencies/agencies';
import {
  invitationsControllerCancelV1,
  invitationsControllerCreateV1,
  invitationsControllerListPendingV1,
} from '@/api/generated/invitations/invitations';
import {
  usersControllerListMembersV1,
  usersControllerRemoveMemberV1,
} from '@/api/generated/users/users';
import type {
  CreateInvitationDto,
  InvitationsControllerListPendingV1Params,
  UsersControllerListMembersV1Params,
} from '@/api/generated/model';

/**
 * Query keys for the settings module.
 *
 * Three separate roots rather than one, because they invalidate independently:
 * inviting someone changes the invitation list but not the member list, while
 * accepting an invitation changes both plus the agency's seat count.
 */
export const settingsKeys = {
  agency: ['settings', 'agency'] as const,
  members: {
    all: ['settings', 'members'] as const,
    lists: () => [...settingsKeys.members.all, 'list'] as const,
    list: (params: UsersControllerListMembersV1Params) =>
      [...settingsKeys.members.lists(), params] as const,
  },
  invitations: {
    all: ['settings', 'invitations'] as const,
    lists: () => [...settingsKeys.invitations.all, 'list'] as const,
    list: (params: InvitationsControllerListPendingV1Params) =>
      [...settingsKeys.invitations.lists(), params] as const,
  },
};

/** The caller's agency. Read-only — no platform endpoint edits agency data. */
export function useMyAgencyQuery() {
  return useQuery({
    queryKey: settingsKeys.agency,
    queryFn: () => agenciesControllerGetMyAgencyV1(),
  });
}

export function useMembersQuery(params: UsersControllerListMembersV1Params) {
  return useQuery({
    queryKey: settingsKeys.members.list(params),
    queryFn: () => usersControllerListMembersV1(params),
    placeholderData: (previous) => previous,
  });
}

export function useInvitationsQuery(params: InvitationsControllerListPendingV1Params) {
  return useQuery({
    queryKey: settingsKeys.invitations.list(params),
    queryFn: () => invitationsControllerListPendingV1(params),
    placeholderData: (previous) => previous,
  });
}

/**
 * Invites someone by email. Owner-only; a member gets a 403.
 *
 * Does not touch the member list — an invitation consumes no seat until it is
 * accepted, so `usedSeats` is unchanged and refetching members would be noise.
 */
export function useInviteMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (dto: CreateInvitationDto) => invitationsControllerCreateV1(dto),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: settingsKeys.invitations.lists() });
    },
  });
}

export function useCancelInvitationMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (invitationId: string) => invitationsControllerCancelV1(invitationId),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: settingsKeys.invitations.lists() });
    },
  });
}

/**
 * Removes a member. Owner-only, and a hard delete of the user account — not
 * just the membership — so the confirmation copy has to be explicit about it.
 *
 * Frees a seat, hence the agency query is invalidated too.
 */
export function useRemoveMemberMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (userId: string) => usersControllerRemoveMemberV1(userId),
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: settingsKeys.members.lists() }),
        queryClient.invalidateQueries({ queryKey: settingsKeys.agency }),
      ]);
    },
  });
}
